// utils/shared/util.js
import { showGlobalModal } from "../layout/GlobalModal";
import MessageManager from "../layout/Message";
import { buildLogoutUrl } from "../shared/utils/logoutRedirect";
import { clearTokenRefreshState, logoutApi } from "../../services/authService";
import { resetAuthSessionState } from "./utils/authSessionState";

const SELECTED_TENANT_ID_KEY = "selectedTenantId";
const SELECTED_ORG_CODE_KEY = "selectedOrgCode";
const SELECTED_ORG_NAME_KEY = "selectedOrganization";

const getStoredValue = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const getPreferredStoredValue = (selectedKey, fallbackKey) => {
  const selectedValue = getStoredValue(selectedKey);
  return selectedValue !== null ? selectedValue : getStoredValue(fallbackKey);
};

const setStorageValue = (key, value) => {
  sessionStorage.setItem(key, String(value ?? ""));
};

export function getOrgCode() {
  return getPreferredStoredValue(SELECTED_ORG_CODE_KEY, "orgCode");
}
export function getTenantId() {
  return getPreferredStoredValue(SELECTED_TENANT_ID_KEY, "tenantId");
}
export function getOrgName() {
  return getPreferredStoredValue(SELECTED_ORG_NAME_KEY, "organization");
}
export function getEmail() {
  return sessionStorage.getItem("email");
}
export function getUserName() {
  return sessionStorage.getItem("userName");
}

export const normalizeOrganizationSelection = (org = {}) => ({
  tenantId: org?.tenantId ?? org?.clientTenantId ?? "",
  orgCode: org?.orgCode ?? org?.OrgCode ?? org?.clientOrgCode ?? "",
  organization: org?.organization ?? org?.clientOrganization ?? "",
});

export const setActiveOrganization = (org = {}) => {
  const selection = normalizeOrganizationSelection(org);

  setStorageValue(SELECTED_TENANT_ID_KEY, selection.tenantId);
  setStorageValue("tenantId", selection.tenantId);
  setStorageValue(SELECTED_ORG_CODE_KEY, selection.orgCode);
  setStorageValue("orgCode", selection.orgCode);
  setStorageValue(SELECTED_ORG_NAME_KEY, selection.organization);
  setStorageValue("organization", selection.organization);

  return selection;
};

export const getActiveOrganizationSelection = () => {
  const selectedTenantId = getStoredValue(SELECTED_TENANT_ID_KEY);

  if (selectedTenantId === null) return null;

  return {
    tenantId: selectedTenantId,
    orgCode: getStoredValue(SELECTED_ORG_CODE_KEY) ?? "",
    organization: getStoredValue(SELECTED_ORG_NAME_KEY) ?? "",
  };
};

export const clearActiveOrganization = () => {
  sessionStorage.removeItem(SELECTED_TENANT_ID_KEY);
  sessionStorage.removeItem(SELECTED_ORG_CODE_KEY);
  sessionStorage.removeItem(SELECTED_ORG_NAME_KEY);
};

const shownBanners = new Map();

// This function handles errors from API calls and displays appropriate messages
// ✅ guard to avoid multiple redirects when many APIs fail together
let redirecting401 = false;

export const handleApiError = (error, options = {}) => {
  const {
    showModal = true,
    fallbackTitle = "Error",
    fallbackMessage = "Unexpected error occurred",
    redirectOn401 = true,

    // ✅ optional knobs
    redirectReason = "session_expired",
    includeReturnTo = true,
    useReplace = true, // use location.replace so back button doesn't return to broken page
  } = options;

  const status = error?.response?.status ?? error?.status ?? error?.code;
  const errorData = error?.response?.data ?? error;

  const renderModal = (title, message) => {
    if (showModal) {
      showGlobalModal({ title, message, type: "error" });
    } else {
      console.warn(`[Silent Error] ${title}: ${message}`);
    }
  };

  const renderBanner = (message) => {
    const bannerKey = `${status}-${message}`;
    const now = Date.now();
    const lastShown = shownBanners.get(bannerKey);

    // If same banner was shown in last 2 seconds, skip it
    if (lastShown && now - lastShown < 2000) {
      console.log("Duplicate banner suppressed:", bannerKey);
      return;
    }

    shownBanners.set(bannerKey, now);

    // Clean up after 5 seconds
    setTimeout(() => {
      shownBanners.delete(bannerKey);
    }, 5000);

    MessageManager.showBanner("error", message, [
      {
        label: "Report",
        type: "primary",
        onClick: () => {
          window.location.href = "/contact";
        },
      },
      {
        label: "Dismiss",
        type: "secondary",
        onClick: () => {
          MessageManager.clearAll();
        },
      },
    ]);
  };

  // 400 - validation
  if (status === 400 && (errorData?.errors || errorData?.message)) {
    const messages = [];
    const errs = errorData.errors;

    if (errs && typeof errs === "object") {
      Object.values(errs).forEach((v) => {
        if (typeof v === "string") messages.push(v);
        else if (v?.msg) messages.push(v.msg);
        else messages.push(JSON.stringify(v));
      });
    }
    if (errorData?.message) messages.push(errorData.message);

    const finalMessage = messages.filter(Boolean).join("\n") || fallbackMessage;
    return renderModal("Validation Error", finalMessage);
  }

  // ✅ 401 - redirect to Auth with session-expired reason
  // if (status === 401) {
  //   if (redirectOn401 && !redirecting401) {
  //     redirecting401 = true;

  //     // clear this app storage (auth storage is separate origin anyway)
  //     sessionStorage.clear();

  //     const returnTo = includeReturnTo ? window.location.href : undefined;

  //     const redirectUrl = buildLogoutUrl({
  //       reason: redirectReason,
  //     });

  //     if (useReplace) window.location.replace(redirectUrl);
  //     else window.location.href = redirectUrl;

  //     return; // stop here (no modal in upskilling)
  //   }

  //   // if redirectOn401 is false, then show modal (fallback)
  //   const msg =
  //     errorData?.errors?.server?.msg || errorData?.message || "Session expired";
  //   return renderModal("Unauthorized", msg);
  // }

  // ✅ 401 - redirect to Auth with session-expired reason
  if (status === 401) {
    if (redirectOn401 && !redirecting401) {
      redirecting401 = true;

      // clear this app storage (auth storage is separate origin anyway)
      clearTokenRefreshState();
      resetAuthSessionState();
      // sessionStorage.clear();

      const returnTo = includeReturnTo ? window.location.href : undefined;
      void logoutApi().catch(() => {});

      const redirectUrl = buildLogoutUrl({
        reason: redirectReason, // session_expired
      });

      if (useReplace) window.location.replace(redirectUrl);
      else window.location.href = redirectUrl;

      return; // stop here
    }

    // if redirectOn401 is false, then show modal (fallback)
    const msg =
      errorData?.errors?.server?.msg || errorData?.message || "Session expired";
    return renderModal("Unauthorized", msg);
  }

  // 500
  if (status === 500) {
    const msg =
      errorData?.errors?.server?.msg ||
      "Something went wrong. Please try again.";
    renderBanner(msg);
    return;
  }

  // fallback
  const fallback =
    errorData?.message ||
    errorData?.fullError ||
    error?.message ||
    fallbackMessage;

  return renderModal(fallbackTitle, fallback);
};

const employeeCreatedDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

export const formatEmployeeCreatedAt = (createdAt) => {
  if (!createdAt) return "-";

  const createdDate = new Date(createdAt);
  if (Number.isNaN(createdDate.getTime())) return "-";

  return employeeCreatedDateFormatter.format(createdDate);
};
